/* eslint-disable camelcase */
import { useMemo } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import InfoBox from '../../../../../components/minside/InfoBox';
import OverviewDaySection from '../../../../../components/minside/OverviewTab/OverviewDaySection';

import { getTodayYyyymmddLocal } from '../../../../../utils/formatDate';
import {
    finnForsteAktivitetsDato,
    hentAktiviteterPerDato
} from '../../../../../utils/minside/activities';
import { hentAlleDatoer } from '../../../../../utils/minside/overview';

function OverviewTab({ data, loading, onNavigate }) {
    const {
        aktiviteter = [],
        lunsj_dager = [],
        middag_dager = [],
        aktivitet_pameld_apent = 'J'
    } = data ?? {};

    const aktiviteterErApne = aktivitet_pameld_apent === 'J';

    const forsteAktivitetsDato = useMemo(
        () => finnForsteAktivitetsDato(aktiviteter),
        [aktiviteter]
    );

    const dagensDato = getTodayYyyymmddLocal();

    const visPameldingStengtInfo =
        !aktiviteterErApne && forsteAktivitetsDato !== null && forsteAktivitetsDato > dagensDato;

    const alleDatoer = useMemo(
        () => hentAlleDatoer(aktiviteter, lunsj_dager, middag_dager),
        [aktiviteter, lunsj_dager, middag_dager]
    );

    const lunsjPerDato = useMemo(
        () => Object.fromEntries(lunsj_dager.map(d => [d.dato, d])),
        [lunsj_dager]
    );

    const middagPerDato = useMemo(
        () => Object.fromEntries(middag_dager.map(d => [d.dato, d])),
        [middag_dager]
    );

    const aktiviteterPerDato = useMemo(() => hentAktiviteterPerDato(aktiviteter), [aktiviteter]);

    const harValgtMinstEnAktivitet = useMemo(
        () => aktiviteter.some(aktivitet => aktivitet.pameldt === 'J'),
        [aktiviteter]
    );

    if (loading) {
        return (
            <div className="data-loader">
                <FontAwesomeIcon icon={faSpinner} spin size="xl" />
            </div>
        );
    }

    if (!alleDatoer.length) {
        return (
            <div className="overview-tab">
                <section className="overview-section">
                    <div className="overview-section__header">
                        <h3 className="overview-section__heading">Mitt opphold</h3>
                        <p className="overview-section__desc">
                            Her ser du aktiviteter og måltider som er registrert.
                        </p>
                    </div>
                    <div className="overview-empty-state">
                        <p className="overview-empty-state__message">
                            Ingen data tilgjengelig ennå
                        </p>
                        <p className="overview-empty-state__sub">
                            Programmet er ikke klart ennå. Sjekk tilbake senere.
                        </p>
                        {aktiviteterErApne && (
                            <button
                                type="button"
                                className="btn btn-small"
                                onClick={() => onNavigate('program')}
                            >
                                Gå til aktiviteter
                            </button>
                        )}
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="overview-tab">
            {visPameldingStengtInfo && (
                <section className="overview-section overview-section--compact">
                    <InfoBox variant="info">
                        <p>
                            Påmeldingen til aktiviteter er nå stengt. Arrangøren går gjennom ønskene
                            og fordeler plasser der det er mulig. Du vil få beskjed når du kan logge
                            inn igjen og se endelig resultat.
                        </p>
                    </InfoBox>
                </section>
            )}

            <section className="overview-section">
                <div className="overview-section__header">
                    <h3 className="overview-section__heading">Mitt opphold</h3>
                    <p className="overview-section__desc">
                        Oversikt over aktiviteter og måltider som er registrert for oppholdet.
                    </p>
                </div>

                {aktiviteterErApne && !harValgtMinstEnAktivitet && (
                    <InfoBox variant="warning">
                        <p>
                            Husk å velge aktiviteter og bestille måltider du ønsker for oppholdet
                            ditt.
                        </p>

                        <button
                            type="button"
                            className="info-box__button info-box__button--warning"
                            onClick={() => onNavigate('program')}
                        >
                            Velg aktiviteter
                        </button>
                    </InfoBox>
                )}

                <div className="overview-days-list">
                    {alleDatoer.map(dato => (
                        <OverviewDaySection
                            key={dato}
                            dato={dato}
                            aktiviteterForDato={aktiviteterPerDato[dato] ?? []}
                            lunsj={lunsjPerDato[dato] ?? null}
                            middag={middagPerDato[dato] ?? null}
                            aktivitetPameldApent={aktivitet_pameld_apent}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}

export default OverviewTab;
